'use client';
import {
  Button,
  ButtonColor,
  formatName,
  pluralize,
  ProfileImage,
  Rating,
  ToastDuration,
  ToastType,
  useToast,
} from '@leland-dev/leland-ui-library';
import { useRouter } from 'next/navigation';
import React, { type FC, useCallback } from 'react';

import {
  CoachVisibility,
  type GoalName,
  MatchedCoachStatus,
  type Scalars,
} from '../../__generated-gql-types__/globalTypes';
import { useAuth } from '../../context/AuthContext';
import { isCoachAvailabilityOnboarded } from '../../utils/availability';
import { getCTATooltipTextForBlockedUser } from '../../utils/coach';
import { useOnce } from '../../utils/hooks/useOnce';
import { useSessionState } from '../../utils/hooks/useSessionState';
import { getUrlString } from '../../utils/url';
import { useLoginModal } from '../LoginModal';
import { usePhoneNumberModal } from '../sms/PhoneNumberModal';

import {
  type CoachBottomMessageCtaCoachFragment,
  CoachBottomMessageCtaGetCoachByIdDocument,
  type CoachBottomMessageCtaUserFragment,
  useCoachBottomMessageCtaGetCoachByIdQuery,
  useCoachBottomMessageCtaGetUserQuery,
  useCoachBottomMessageCtaLinkedInSelfServeRequestMutation,
} from './__generated-gql-types__/CoachBottomMessageCTA.generated';
import { useMessageModal } from './modals/MessageModal';
import { useRequestCoachMatchModal } from './modals/RequestCoachMatchModal';
import { useScheduleModal } from './modals/ScheduleModal';

enum CTAType {
  MESSAGE,
  SCHEDULE,
}

interface CoachBottomMessageCTAProps {
  user: Possible<CoachBottomMessageCtaUserFragment>;
  isVisible: boolean;
  coach: CoachBottomMessageCtaCoachFragment;
  viewAllReviews: () => void;
  goal: Possible<GoalName>;
  categoryUrn: Nullable<Scalars['CategoryUrn']['output']>;
  categorySlug: Nullable<string>;
  blocked?: boolean;
}

const CoachBottomMessageCTA: FC<CoachBottomMessageCTAProps> = ({
  user,
  isVisible,
  coach: coachProp,
  viewAllReviews,
  categoryUrn,
  categorySlug,
  blocked,
  goal,
}) => {
  const router = useRouter();
  const { showToast } = useToast();
  const { data } = useCoachBottomMessageCtaGetUserQuery();
  const currentUser = data?.user ?? user;
  const { currentUser: authUser } = useAuth();

  const { data: hydratedCoachData } = useCoachBottomMessageCtaGetCoachByIdQuery(
    { variables: { coachUrn: coachProp.id } },
  );
  const coach = hydratedCoachData?.coach ?? coachProp;

  const [requestMatchAction] =
    useCoachBottomMessageCtaLinkedInSelfServeRequestMutation();

  const { setOpen: setLoginModalOpen, Modal: LoginModal } = useLoginModal();
  const { setOpen: setMessageModalOpen, Modal: MessageModal } =
    useMessageModal();
  const { setOpen: setScheduleModalOpen, Modal: ScheduleModal } =
    useScheduleModal();
  const {
    openModalIfNecessary: openPhoneNumberModalIfNecessary,
    Modal: PhoneNumberModal,
  } = usePhoneNumberModal();
  const {
    setOpen: setRequestCoachMatchModalOpen,
    Modal: RequestCoachMatchModal,
  } = useRequestCoachMatchModal();

  const [pendingAction, setPendingAction] = useSessionState<CTAType | null>({
    key: 'coach-bottom-cta-pending-action',
  });

  const isLoggedIn = !!currentUser?.applicant;
  const coachingRelationshipExists = !!coach?.coachingRelationship;
  const hasBookedWithCoach = !!coach?.mostRecentOrder;
  const onTrialOrder = !!currentUser?.applicant?.hasUnassignedTrialOrder;

  const isRequestedMatch =
    coach.matchedCoach?.status === MatchedCoachStatus.REQUESTED;
  const isDeclinedMatch =
    coach.matchedCoach?.status === MatchedCoachStatus.DECLINED;

  const canPurchase =
    coach?.visibility !== CoachVisibility.PRIVATE_CLOSED || hasBookedWithCoach;

  const showAvailabilityUI =
    canPurchase &&
    coach?.availability &&
    isCoachAvailabilityOnboarded(coach.availability.defaultAvailability);

  const blockedUserCTAText = getCTATooltipTextForBlockedUser(
    coach.user.firstName,
  );

  const handleRequestLinkedinMatch = useCallback(() => {
    if (!currentUser?.applicant || !categoryUrn) {
      return;
    }
    void requestMatchAction({
      variables: {
        input: {
          applicantUrn: currentUser?.applicant.id,
          coachUrn: coach.id,
          status: MatchedCoachStatus.REQUESTED,
          categoryUrn,
        },
      },
      optimisticResponse: {
        createOpportunity: {
          id: 'temp-id',
          status: MatchedCoachStatus.REQUESTED,
          coach: {
            id: coach.id,
            matchedCoach: {
              id: 'temp-id',
              status: MatchedCoachStatus.REQUESTED,
            },
          },
        },
      },
    });
    showToast({
      type: ToastType.SUCCESS,
      message: 'Request sent',
      duration: ToastDuration.NORMAL,
    });
  }, [
    categoryUrn,
    coach.id,
    currentUser?.applicant,
    requestMatchAction,
    showToast,
  ]);

  const onMessage = useCallback(() => {
    if (coachingRelationshipExists) {
      return router?.push(getUrlString('/inbox', { coach: coach.id }));
    } else {
      setMessageModalOpen(true);
    }
  }, [coachingRelationshipExists, coach.id, router, setMessageModalOpen]);

  const handleMessageCoach = () => {
    if (isLoggedIn) {
      return onMessage();
    } else {
      setPendingAction(CTAType.MESSAGE);
      setLoginModalOpen(true);
    }
  };

  const handleSchedule = () => {
    if (isLoggedIn) {
      setScheduleModalOpen(true);
    } else {
      setPendingAction(CTAType.SCHEDULE);
      setLoginModalOpen(true);
    }
  };

  const handleRequestCoachMatch = () => {
    if (onTrialOrder) {
      void handleRequestLinkedinMatch();
    } else {
      setRequestCoachMatchModalOpen(true);
    }
  };

  const noContactForNewClient =
    coach.visibility === CoachVisibility.PRIVATE_CLOSED &&
    !coachingRelationshipExists;

  // Handle post-authentication actions
  useOnce(() => {
    if (authUser && pendingAction) {
      if (pendingAction === CTAType.SCHEDULE) {
        setScheduleModalOpen(true);
      } else if (pendingAction === CTAType.MESSAGE) {
        void onMessage();
      }
      setPendingAction(null);
    }
  }, !!authUser && !!pendingAction);

  // hide if user is on trial order and has requested match
  return onTrialOrder && isRequestedMatch ? null : (
    <>
      <div
        className={`fixed bottom-0 flex w-full items-center justify-between border-t border-leland-gray-stroke bg-white px-4 py-5 transition duration-200 ease-in-out md:hidden ${
          isVisible ? 'bottom-message-cta-visible' : 'bottom-message-cta-hidden'
        }`}
      >
        <div className="flex items-center justify-start">
          <ProfileImage className="size-12" {...coach.user} />
          <div className="ml-3 flex flex-col items-start justify-center">
            <h2 className="text-left text-xl font-medium text-leland-gray-dark">
              {formatName({
                firstName: coach.user.firstName,
                lastName: coach.user.lastName,
              })}
            </h2>
            {coach.reviewTotal > 0 ? (
              <div className="flex items-center justify-start">
                <Rating
                  totalStars={1}
                  rate={coach.averageReviewRate}
                  showRate
                />
                <div className="mx-2 mt-0.5 block size-0.5 rounded bg-leland-gray-light" />
                <button
                  type="button"
                  className="whitespace-nowrap text-base font-medium text-leland-primary underline"
                  onClick={viewAllReviews}
                  id="coach_bottom_message--view_reviews"
                >
                  {pluralize(coach.reviewTotal, 'review')}
                </button>
              </div>
            ) : null}
          </div>
        </div>
        {isDeclinedMatch || noContactForNewClient ? null : isRequestedMatch ||
          onTrialOrder ? (
          goal && categoryUrn ? (
            <Button
              label={isRequestedMatch ? 'Match requested' : 'Request match'}
              buttonColor={
                isRequestedMatch ? ButtonColor.WHITE : ButtonColor.PRIMARY
              }
              onClick={handleRequestCoachMatch}
              rounded
              disabled={blocked || isRequestedMatch}
              tooltipContent={blocked ? blockedUserCTAText : null}
              id="coach_bottom_message--request_match"
            />
          ) : null
        ) : (
          <Button
            label={showAvailabilityUI ? 'Schedule' : 'Message'}
            buttonColor={ButtonColor.PRIMARY}
            disabled={blocked}
            tooltipContent={blocked ? blockedUserCTAText : null}
            rounded
            onClick={showAvailabilityUI ? handleSchedule : handleMessageCoach}
            id={
              showAvailabilityUI
                ? 'coach_bottom_message--schedule'
                : 'coach_bottom_message--message'
            }
          />
        )}
      </div>

      {/* MODALS */}
      <LoginModal />

      <MessageModal
        coach={coach}
        categoryUrn={categoryUrn}
        onSuccess={() => setMessageModalOpen(false)}
      />

      <ScheduleModal
        coach={coach}
        categoryUrn={categoryUrn}
        categorySlug={categorySlug}
        refetchQueries={[CoachBottomMessageCtaGetCoachByIdDocument]}
        onSuccessModalDismiss={openPhoneNumberModalIfNecessary}
      />

      <PhoneNumberModal />

      {currentUser?.applicant?.id && categoryUrn && goal ? (
        <RequestCoachMatchModal
          goal={goal}
          coach={coach}
          applicantUrn={currentUser.applicant.id}
          categoryUrn={categoryUrn}
        />
      ) : null}
    </>
  );
};

export default CoachBottomMessageCTA;
